import { Button, Icon } from "@legal-500/legal500-design-system";
import { useMemo } from "react";
import type { PackageWithFeatureCodes } from "../../../api/packages.service";
import styles from "../PackageVersionModal.module.css";

export type Step3DiffConfirmationProps = {
  package: PackageWithFeatureCodes;
  selectedFeatureCodes: string[];
  onCreateVersion: () => void;
  onBackToEdit: () => void;
  onCancel: () => void;
  isCreating: boolean;
  error: string | null;
};

type FeatureDiff = {
  unchanged: string[];
  added: string[];
  removed: string[];
};

function prepareFeatureDiff(oldFeatures: string[], newFeatures: string[]): FeatureDiff {
  const oldSet = new Set(oldFeatures);
  const newSet = new Set(newFeatures);

  return {
    unchanged: oldFeatures.filter(feature => newSet.has(feature)),
    added: newFeatures.filter(feature => !oldSet.has(feature)),
    removed: oldFeatures.filter(feature => !newSet.has(feature)),
  };
}

export function Step3DiffConfirmation({
  package: pkg,
  selectedFeatureCodes,
  onCreateVersion,
  onBackToEdit,
  onCancel,
  isCreating,
  error,
}: Step3DiffConfirmationProps) {
  const nextVersion = pkg.version + 1;

  const featureDiff = useMemo(() => {
    return prepareFeatureDiff(pkg.featureCodes || [], selectedFeatureCodes);
  }, [pkg.featureCodes, selectedFeatureCodes]);

  const hasChanges = featureDiff.added.length > 0 || featureDiff.removed.length > 0;

  return (
    <>
      <div className={styles.modalHeader}>
        <div className={styles.headerContent}>
          <div className={styles.iconWrapper} aria-hidden="true">
            <Icon icon="copy" size="large" />
          </div>
          <h2 id="version-package-title" className={styles.modalTitle}>
            Confirm Version {nextVersion}
          </h2>
        </div>
        <button
          className={styles.modalCloseButton}
          onClick={onCancel}
          disabled={isCreating}
          aria-label="Close modal"
          type="button"
        >
          <Icon icon="cross" size="small" />
        </button>
      </div>

      <div className={styles.modalBody}>
        <p className={styles.versionMessage}>Creating version {nextVersion} of:</p>
        <p className={styles.packageName}>
          <strong>
            {pkg.name} ({pkg.productCode})
          </strong>
        </p>

        <div className={styles.featureDiffSection}>
          <h3 className={styles.featureDiffTitle}>📋 Feature Changes:</h3>
          <div className={styles.featureDiffBox}>
            {featureDiff.unchanged.length > 0 && (
              <div className={styles.diffGroup}>
                <div className={styles.diffHeaderUnchanged}>
                  ✅ Unchanged ({featureDiff.unchanged.length}):
                </div>
                <ul className={styles.diffList}>
                  {featureDiff.unchanged.map(feature => (
                    <li key={feature} className={styles.diffListItemUnchanged}>
                      • {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {featureDiff.added.length > 0 && (
              <div className={styles.diffGroup}>
                <div className={styles.diffHeaderAdded}>➕ Added ({featureDiff.added.length}):</div>
                <ul className={styles.diffList}>
                  {featureDiff.added.map(feature => (
                    <li key={feature} className={styles.diffListItemAdded}>
                      • {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {featureDiff.removed.length > 0 && (
              <div className={styles.diffGroup}>
                <div className={styles.diffHeaderRemoved}>
                  ➖ Removed ({featureDiff.removed.length}):
                </div>
                <ul className={styles.diffList}>
                  {featureDiff.removed.map(feature => (
                    <li key={feature} className={styles.diffListItemRemoved}>
                      • {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!hasChanges && (
              <div className={styles.noChangesInfo}>
                <Icon icon="info-circle" size="small" />
                <p>No feature changes. You can modify features after creating this version.</p>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className={styles.errorMessage} role="alert">
            {error}
          </div>
        )}
      </div>

      <div className={styles.deactivationWarning}>
        <div className={styles.warningIcon} aria-hidden="true">
          <Icon icon="exclamation" size="small" />
        </div>
        <p>Version {pkg.version} will be deactivated</p>
      </div>

      <div className={styles.modalFooter}>
        <Button variant="outlined" color="default" onClick={onBackToEdit} disabled={isCreating}>
          Back to Edit
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onCreateVersion}
          disabled={isCreating}
          className={styles.primaryButton}
        >
          {isCreating ? (
            <>
              <span className={styles.spinner} />
              Creating...
            </>
          ) : (
            `Create Version ${nextVersion}`
          )}
        </Button>
      </div>
    </>
  );
}
